import React, { useState, useRef, useLayoutEffect } from "react";
import SearchInputTable from "../common/SearchInputTable";
import styles from "../../_css/UsersTable.module.css";
import {
  MoreVertical,
  User,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Eye,
} from "lucide-react";
import SelectComponentWithTabs from "../common/SelectComponentWithTabs";
import { CustomButtonOutline } from "../UI/CustomButtons";
import UserProfileModal from "../UI/UserProfileModal";
import DeleteUserModal from "../UI/DeleteUserModal";
import { createPortal } from "react-dom";
import FilterBar from "../UI/FilterBar";
import CustomTooltip from "../common/CustomTooltip";
import StarReview from "../common/StarReview";
import ApprovePopup from "./ApprovePopup";
import RejectReviewPopup from "./RejectReviewPopup";
import ReviewCarouselPopup from "./ReviewCarouselPopup";
import { formatDate } from "../helper/formatters";
import _ from "lodash";
import {
  deleteAuthToken,
  notifyError,
  notifySuccess,
} from "../../utils/helper";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { updateReviewStatus } from "../../requests";
import { useDispatch } from "react-redux";

const PAGE_SIZE = 25;
const TOTAL_ENTRIES = 20;
const DROPDOWN_WIDTH = 180; // px

const dropdownMenuStyles = {
  position: "absolute",
  top: "36px",
  right: "0",
  minWidth: "180px",
  width: "180px",
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  zIndex: 10,
  padding: "6px 0",
  border: "1px solid #eaf6ff",
};
const dropdownOptionStyles = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 18px",
  fontSize: "13px",
  color: "#6B7280",
  //   background: "#F2F9FD",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  margin: "4px 8px",
  transition: "background 0.2s",
};
const dropdownOptionHover = {
  background: "#F3F4F6",
};

const groupedOptions2 = [
  {
    label: "By Name",
    options: [
      { value: "Name ASC", label: "Name: A to Z" },
      { value: "Name DESC", label: "Name: Z to A" },
    ],
  },
  {
    label: "By Sessions",
    options: [
      { value: "Session ASC", label: "Name: A to Z" },
      { value: "Session DESC", label: "Name: Z to A" },
    ],
  },
];

function RowActionsDropdown({
  onApproveReview,
  onRejectReview,
  onViewReview,
  onRemoveReview,
}) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 180,
  });

  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX - DROPDOWN_WIDTH,
      });
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (btnRef.current && btnRef.current.contains(e.target)) {
        return;
      }
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        className={styles.rowActionsBtn}
        onClick={() => setOpen((o) => !o)}
        tabIndex={0}
        aria-label="Row actions"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
        ref={btnRef}
      >
        <MoreVertical size={22} color="#798797" />
      </button>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              ...dropdownMenuStyles,
              position: "absolute",
              top: dropdownPos.top,
              left: dropdownPos.left,
              minWidth: "180px",
              width: "180px",
              zIndex: 9999,
            }}
          >
            <div
              style={
                hovered === 0
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(0)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                onApproveReview && onApproveReview();
              }}
            >
              <Check size={18} /> Approve Review
            </div>
            <div
              style={
                hovered === 1
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(1)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                onRejectReview && onRejectReview();
              }}
            >
              <X size={18} /> Reject Review
            </div>
            <div
              style={
                hovered === 2
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(2)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                onViewReview && onViewReview();
              }}
            >
              <Eye size={18} /> View Review
            </div>
            <div
              style={
                hovered === 3
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(3)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                onRemoveReview && onRemoveReview();
              }}
            >
              <Trash2 size={18} /> Remove Review
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

const ReviewTable = ({
  headers,
  rows,
  handleSearchChange,
  searchValue,
  searchConfig,
  variant = "blue",
  onPageChange,
  pagination,
  handleDateFilterChange,
  dateFilter,
  handleFilterChange,
  handleSorting,
  refreshTable,
}) => {
  const [page, setPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]); // store indices of selected rows
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [batchIds, setBatchIds] = useState([]);
  const [approvePopupOpen, setApprovePopupOpen] = useState(false);
  const [rejectReviewPopupOpen, setRejectReviewPopupOpen] = useState(false);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const dispatch = useDispatch();
  // Pagination logic
  const pagedRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = pagination?.totalPages || 1;
  const totalEntries = pagination?.totalItems || rows.length;
  const currentPage = pagination?.currentPage || 1;
  const itemsPerPage = pagination?.itemsPerPage || PAGE_SIZE;

  // Map pagedRows to their global indices
  const pagedRowIndices = pagedRows.map(
    (_, idx) => (page - 1) * PAGE_SIZE + idx
  );

  const allVisibleSelected = pagedRowIndices.every((idx) =>
    selectedRows.includes(idx)
  );
  const someVisibleSelected = pagedRowIndices.some((idx) =>
    selectedRows.includes(idx)
  );

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      setBatchIds([]);
      setSelectedRows(
        selectedRows.filter((idx) => !pagedRowIndices.includes(idx))
      );
    } else {
      setBatchIds(rows.map((row) => row.id));
      setSelectedRows([
        ...selectedRows.filter((idx) => !pagedRowIndices.includes(idx)),
        ...pagedRowIndices,
      ]);
    }
  };

  const handleSelectRow = (rowIdx, rowId) => {
    if (batchIds.includes(rowId)) {
      setBatchIds(batchIds.filter((id) => id !== rowId));
    } else {
      setBatchIds([...batchIds, rowId]);
    }
    if (selectedRows.includes(rowIdx)) {
      setSelectedRows(selectedRows.filter((idx) => idx !== rowIdx));
    } else {
      setSelectedRows([...selectedRows, rowIdx]);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && onPageChange) {
      onPageChange(newPage, itemsPerPage);
    }
  };

  // Helper to get user data for modal (mocked for now)
  const getUserData = (rowIdx) => {
    // You should replace this with real data mapping

    const row = rows[rowIdx] || [];
    return {
      avatar: row[0]?.avatar, // if you have avatar in your data
      fullName: row[0],
      email: row[1],
      phoneNumber: row[2],
      designation: row[3],
      language: row[8],
      joinDate: row[9],
      subscription: `${row[5]} user`,
    };
  };

  const handleReviewUpdate = (review) => {
    setCarouselOpen(false);
    if (review === 1) {
      setApprovePopupOpen(true);
    } else {
      setRejectReviewPopupOpen(true);
    }
    setCarouselOpen(false);
  };

  const getReviewSnippet = (cell) => {
    if (cell.length > 20) {
      return cell.slice(0, 20) + "...";
    }
    return cell;
  };

  const formatColumnData = (data, key) => {
    // TODO: Add more keys to the arrays
    const capitalizeKeys = ["language", "sessions", "top_session", "sub_type"];
    const statusKeys = ["Pending", "Approved", "Rejected"];
    const nameKeys = ["full_name", "session_title", "review"];
    // TODO: Add logic for keys
    if (key === "created_at") return formatDate(data);
    if (capitalizeKeys.includes(key)) return _.capitalize(data);
    if (key === "ratings") return <StarReview rating={data} />;
    if (nameKeys.includes(key))
      return (
        <CustomTooltip content={data} placement="bottom">
          <span>{getReviewSnippet(data)}</span>
        </CustomTooltip>
      );
    if (key === "status")
      return (
        <span className={styles[statusKeys[data]]}>{statusKeys[data]}</span>
      );

    return data;
  };

  const handleReviewStatusUpdate = async (
    review,
    status,
    adminComment = ""
  ) => {
    dispatch(startLoader());
    try {
      const response = await updateReviewStatus({
        review_id: review, // Always send as array
        status: status,
        adminComment,
      });

      if (response.status === 200) {
        if (status === 1) {
          setApprovePopupOpen(true);
        } else if (status === 2) {
          setRejectReviewPopupOpen(true);
        } else if (status === 9) {
          setDeleteModalOpen(false);
          notifySuccess(response.data.message);
        }

        setSelectedRows([]);
        setBatchIds([]);
        setCarouselOpen(false)
        refreshTable();
      } else {
        notifyError("Failed to update review status");
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.log(error);
    }
    dispatch(stopLoader());
  };

  const handleBatchIds = (rowId) => {
    if (batchIds.length === 0 || !batchIds.includes(rowId)) {
      return [rowId];
    }
    return batchIds;
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.filtersRow}>
        <div className="d-flex align-items-center gap-3">
          <SelectComponentWithTabs
            options={[
              { value: "All", label: "All" },
              { value: "1", label: "1 Star" },
              { value: "2", label: "2 Star" },
              { value: "3", label: "3 Star" },
              { value: "4", label: "4 Star" },
              { value: "5", label: "5 Star" },
            ]}
            placeholder="Rating"
            handleValueChange={(value) => {
              handleFilterChange("ratings", value);
            }}
            disabled={false}
            isMulti={false}
            isClearable={false}
            value={null}
          />
          <SelectComponentWithTabs
            options={groupedOptions2}
            placeholder="Select Sort By"
            handleValueChange={handleSorting}
            disabled={false}
            isMulti={false}
            isClearable={false}
            value={null}
          />
          <FilterBar
            onFilterChange={handleDateFilterChange}
            showExtraFilter={false}
            filter={dateFilter}
          />
          <SelectComponentWithTabs
            options={[
              { value: "", label: "All" },
              { value: 1, label: "Approved" },
              { value: 2, label: "Rejected" },
              { value: 0, label: "Pending" },
            ]}
            placeholder="Status"
            handleValueChange={(value) => {
              handleFilterChange("status", value);
            }}
            disabled={false}
            isMulti={false}
            isClearable={false}
            value={null}
          />
        </div>
        <SearchInputTable
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={searchConfig.placeholder}
          column={searchConfig.column}
          padding="0px 8px"
          paddingContainer="0 8px 0 0"
        />
      </div>
      <hr />
      <div className={styles.tableWrapper}>
        <div className={styles.tableBorderWrapper}>
          <table className={styles.usersTable}>
            <thead>
              <tr
                style={{
                  background: variant === "blue" ? "#F3FAFF" : "#F0FDF4",
                }}
              >
                <th
                  style={{
                    width: "12px",
                    color: variant === "blue" ? "#174291" : "#14532B",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          !allVisibleSelected && someVisibleSelected;
                    }}
                    onChange={handleSelectAll}
                    className={styles.checkbox}
                  />
                  <label style={{ marginLeft: "8px" }}>S.L</label>
                </th>

                {headers.map((header, idx) => (
                  <th
                    style={{
                      color: variant === "blue" ? "#174291" : "#14532B",
                    }}
                    key={idx}
                  >
                    {header.label}
                  </th>
                ))}

                <th
                  style={{
                    width: "12px",
                    color: variant === "blue" ? "#174291" : "#14532B",
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row, idx) => {
                const globalIdx = (page - 1) * PAGE_SIZE + idx;
                return (
                  <tr key={globalIdx}>
                    <td className={styles.tableCell} style={{ width: "12px" }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(globalIdx)}
                        onChange={() => handleSelectRow(globalIdx, row.id)}
                        className={styles.checkbox}
                      />
                      <span style={{ marginLeft: "8px" }}>{globalIdx + 1}</span>
                    </td>

                    {/* {row.map((cell, cidx) => (
                      <td className={styles.tableCell} key={cidx}>
                        {["Active", "Expired"].includes(cell) ? (
                          <span
                            className={
                              cell === "Active"
                                ? styles.activeStatusBadge
                                : styles.expiredStatusBadge
                            }
                          >
                            {cell}
                          </span>
                        ) : headers[cidx] === "Review Snippet" ? (
                          <CustomTooltip content={cell} placement="bottom">
                            <span>{getReviewSnippet(cell)}</span>
                          </CustomTooltip>
                        ) : headers[cidx] === "Rating" ? (
                          <StarReview rating={cell} />
                        ) : headers[cidx] === "Status" ? (
                          <span className={styles[cell]}>{cell}</span>
                        ) : (
                          <span>{cell}</span>
                        )}
                      </td>
                    ))} */}
                    {headers.map((header, idxIn) => (
                      <td key={idxIn} className={styles.tableCell}>
                        {formatColumnData(row[header.key], header.key)}
                      </td>
                    ))}

                    <td>
                      <RowActionsDropdown
                        onApproveReview={() => {
                          handleReviewStatusUpdate(handleBatchIds(row.id), 1);
                        }}
                        onRejectReview={() => {
                          handleReviewStatusUpdate(handleBatchIds(row.id), 2);
                        }}
                        onViewReview={() => {
                          setCarouselOpen(true);
                          setCarouselIndex(idx);
                        }}
                        onRemoveReview={() => {
                          setBatchIds(handleBatchIds(row.id));
                          setDeleteModalOpen(true);
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className={styles.tableFooter}>
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, totalEntries)} of {totalEntries}{" "}
          entries
        </div>
        <div className={styles.paginationRow}>
          <button
            className={styles.paginationBtn}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={18} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={
                currentPage === i + 1
                  ? `${styles.paginationBtn} ${styles.paginationBtnActive}`
                  : styles.paginationBtn
              }
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1 < 10 ? `0${i + 1}` : i + 1}
            </button>
          ))}
          <button
            className={styles.paginationBtn}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <UserProfileModal
        show={profileModalOpen}
        onHide={() => setProfileModalOpen(false)}
        user={selectedProfile}
      />
      <ApprovePopup
        show={approvePopupOpen}
        onHide={() => setApprovePopupOpen(false)}
      />
      <RejectReviewPopup
        show={rejectReviewPopupOpen}
        onHide={() => setRejectReviewPopupOpen(false)}
      />
      <ReviewCarouselPopup
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        reviews={pagedRows.map((row) => ({
          id: row?.id,
          avatar: row?.profile_url || "https://picsum.photos/200",
          fullName: row?.full_name || "N/A",
          email: row?.email,
          date: row?.created_at,
          category: row?.category,
          sessionTitle: row?.session_title,
          rating: row?.ratings,
          content: row?.review,
          status: row?.status,
          language: row?.language,
          review_count: row?.review_count,
          reviewd_by: row?.reviewed_by,
          adminComment: row?.reviewer_comment,
        }))}
        initialIndex={carouselIndex}
        handleReviewUpdate={handleReviewUpdate}
        handleReviewStatusUpdate={handleReviewStatusUpdate}
        approvePopupOpen={approvePopupOpen}
        rejectReviewPopupOpen={rejectReviewPopupOpen}
      />
      <DeleteUserModal
        show={deleteModalOpen}
        onHide={() => setDeleteModalOpen(false)}
        onDelete={() => handleReviewStatusUpdate(batchIds, 9)}
        isMultiple={batchIds.length > 1 && batchIds.includes(batchIds)}
        count={batchIds.length}
        size="sm"
        btnText="Delete Review"
        title="Remove Review"
        message="Are you sure you want to delete this review? This action is 
permanent and cannot be undone."
      />
    </div>
  );
};

export default ReviewTable;
